import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import classnames from 'classnames';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import FluidButton from 'ui/FluidButton';
import SaveIcon from 'ui/Icons/components/Save.component';
import DownloadIcon from 'ui/Icons/components/Download.component';
import { useCurrentWidth } from 'effects';
import { RIGHT_COLUMN_BREAKPOINT } from './const';
import { makeBackendApi } from 'services/BackendApi';
import { useQueryClient } from '@tanstack/react-query';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import * as BookingBuilderSelectors from 'store/modules/bookingBuilder/selectors';
import * as UiActions from 'store/modules/ui/actions';
import { selectedTaSelector } from 'store/modules/agents';
import { actingOnBehalfOfUserSelector } from 'store/modules/actingOnBehalfOf/selectors';
import { selectedCompanyMembershipSelector } from 'store/modules/agents/selectors';
import { useNotifications } from 'hooks/useNotifications';
import { ButtonWithIcon } from './components/ButtonWithIcon';
import { IBasketBuildL4 } from 'services/BackendApi/types/Basket';
import { IBookingInformation } from 'interfaces';
import { allBuildsShareCurrency, buildCreateMultiPayload, submitMultiBooking } from './multiHelpers';
import { TCreateMultiResult } from 'services/BackendApi/types/MultiBooking';
import DownloadQuoteMarginModal from 'containers/SummaryForm/DownloadQuoteMarginModal';
import { EPdfLogo } from 'store/modules/bookingManager/subdomains/breakdown/model';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { MobileBottomDrawer } from 'ui/MobileBottomDrawer/MobileBottomDrawer';

interface IProps {
  selectedBuilds: IBasketBuildL4[];
  selectedTaUserUuid: string | null;
  isRequestInProgress: boolean;
  setRequestInProgress: Dispatch<SetStateAction<boolean>>;
  setShouldShowLeaveAlert: Dispatch<SetStateAction<boolean>>;
  setRedirectLocation: Dispatch<SetStateAction<string | null>>;
  setShowValidation: Dispatch<SetStateAction<boolean>>;
  canBook: boolean;
}

type Action = 'save' | 'request' | 'quote';

export const BasketMultiBookingButtons: React.FC<IProps> = ({
  selectedBuilds,
  selectedTaUserUuid,
  isRequestInProgress,
  setRequestInProgress,
  setShouldShowLeaveAlert,
  setRedirectLocation,
  setShowValidation,
  canBook,
}) => {
  const { currentWidth } = useCurrentWidth();
  const { isMobile } = useResponsive();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showErrorNotification } = useNotifications();
  const backendApi = makeBackendApi(selectedTaUserUuid ?? undefined);
  const bookingManagerApi = makeBookingManagerApi();

  const guestInfoRaw = useSelector(BookingBuilderSelectors.guestInfoSelector);
  const guestInfo: IBookingInformation = {
    guestTitle: guestInfoRaw.guestTitle ?? undefined,
    guestFirstName: guestInfoRaw.guestFirstName ?? undefined,
    guestLastName: guestInfoRaw.guestLastName ?? undefined,
    isRepeatGuest: guestInfoRaw.isRepeatGuest ?? false,
    comments: guestInfoRaw.comments ?? undefined,
  };
  const selectedTa = useSelector(selectedTaSelector);
  const actingOnBehalfOfUser = useSelector(actingOnBehalfOfUserSelector);
  const selectedCompanyMembership = useSelector(selectedCompanyMembershipSelector);

  const [pendingAction, setPendingAction] = useState<Action | null>(null);
  const [isQuoteMarginSelectorVisible, setIsQuoteMarginSelectorVisible] = useState(false);

  const guestInfoFilledIn = !!(guestInfo.guestFirstName?.trim() && guestInfo.guestLastName?.trim());
  const buttonsDisabled = !canBook || isRequestInProgress;

  const submit = useCallback(
    async (action: Action, quoteLogoOption: EPdfLogo | null = null) => {
      if (!guestInfoFilledIn) {
        setShowValidation(true);
        return;
      }
      // Currency mismatch is handled in the right-column; CTAs are
      // already disabled, but adding this as a defensive guard.
      if (!allBuildsShareCurrency(selectedBuilds)) {
        showErrorNotification('Multiple selection without same currency is not allowed.');
        return;
      }

      setPendingAction(action);
      setRequestInProgress(true);
      try {
        const status = action === 'request' ? 'requested' : 'potential';
        const payload = buildCreateMultiPayload({
          builds: selectedBuilds,
          guestInfo,
          status,
          selectedTaUuid: selectedTa?.uuid,
          companyMembershipUuid: selectedCompanyMembership?.uuid ?? null,
          isQuote: action === 'quote',
        });
        const result: TCreateMultiResult = await submitMultiBooking({
          backendApi,
          payload,
          clientCountryCode: selectedBuilds[0]?.clientCountryCode ?? null,
          actingOnBehalfOfUser,
        });

        if (!result.success) {
          showErrorNotification(result.error || 'Failed to create multi-hotel booking.');
          return;
        }

        if (action === 'quote') {
          let pdfRes;
          if (quoteLogoOption === EPdfLogo.MAIN_COMPANY_LOGO || quoteLogoOption === null) {
            pdfRes = await bookingManagerApi.generateBreakdownPdfForTa(result.uuid, true);
          } else {
            pdfRes = await bookingManagerApi.generateBreakdownPdfForClient(result.uuid, quoteLogoOption, true);
          }
          const upload: { url: string; filename: string } = pdfRes.data;
          saveAs(upload.url, upload.filename);
          dispatch(
            UiActions.enqueueNotification({
              message: `Quote ${result.humanReadableId} created`,
              options: { variant: 'success' },
            })
          );
          setIsQuoteMarginSelectorVisible(false);
          return;
        }

        queryClient.invalidateQueries({ queryKey: ['basket'] });
        setShouldShowLeaveAlert(false);
        setRedirectLocation(`/booking-manager/${result.uuid}`);
      } catch (e) {
        const message =
          e?.response?.data?.errors?.[0]?.message || e?.message || 'Failed to create multi-hotel booking.';
        showErrorNotification(message);
      } finally {
        setRequestInProgress(false);
        setPendingAction(null);
      }
    },
    [
      guestInfoFilledIn,
      selectedBuilds,
      guestInfo,
      selectedTa,
      selectedCompanyMembership,
      backendApi,
      bookingManagerApi,
      actingOnBehalfOfUser,
      dispatch,
      queryClient,
      setRedirectLocation,
      setShouldShowLeaveAlert,
      setShowValidation,
      setRequestInProgress,
      showErrorNotification,
    ]
  );

  const handleQuoteClick = useCallback(() => {
    if (!guestInfoFilledIn) {
      setShowValidation(true);
      return;
    }
    setIsQuoteMarginSelectorVisible(true);
  }, [guestInfoFilledIn, setShowValidation]);

  const handleQuoteModalDownload = useCallback(
    (option: EPdfLogo | null) => {
      submit('quote', option);
    },
    [submit]
  );

  const handleQuoteModalClose = useCallback(() => setIsQuoteMarginSelectorVisible(false), []);

  return (
    <div className="booking-buttons-container">
      <div className="booking-buttons flex flex-col gap-[8px] mt-[14px]">
        <div
          className={classnames('booking-buttons-primary flex gap-[8px]', {
            'flex-col': currentWidth < RIGHT_COLUMN_BREAKPOINT,
          })}
        >
          <FluidButton
            type="primary"
            className={classnames(
              'booking-button-request-to-book flex-1 w-full font-semibold font-hurmegeometric-sans min-h-[55px] rounded-none uppercase',
              { 'hover:bg-green-prime hover:border-none': !buttonsDisabled }
            )}
            onClick={() => {
              if (!guestInfoFilledIn) {
                setShowValidation(true);
              } else {
                submit('request');
              }
            }}
            disabled={buttonsDisabled}
            isLoading={pendingAction === 'request'}
          >
            Request to Book
          </FluidButton>
        </div>

        <div className="save-quote-proposal-buttons-container">
          <div className="save-quote-proposal-buttons flex gap-[8px]">
            <ButtonWithIcon
              caption="Save"
              className="booking-button-save"
              Icon={SaveIcon}
              onClick={() => submit('save')}
              disabled={buttonsDisabled}
              isLoading={pendingAction === 'save'}
            />
            <ButtonWithIcon
              caption="Quote"
              Icon={DownloadIcon}
              className="booking-button-quote"
              onClick={handleQuoteClick}
              disabled={buttonsDisabled}
              isLoading={pendingAction === 'quote'}
            />
          </div>
        </div>
      </div>

      {isQuoteMarginSelectorVisible &&
        (isMobile ? (
          <MobileBottomDrawer
            isOpen={isQuoteMarginSelectorVisible}
            onClose={handleQuoteModalClose}
            title="Recipient"
          >
            <DownloadQuoteMarginModal
              isQuoteDownloading={pendingAction === 'quote'}
              onClose={handleQuoteModalClose}
              onDownload={handleQuoteModalDownload}
              renderAsContent
            />
          </MobileBottomDrawer>
        ) : (
          <DownloadQuoteMarginModal
            isQuoteDownloading={pendingAction === 'quote'}
            onClose={handleQuoteModalClose}
            onDownload={handleQuoteModalDownload}
          />
        ))}
    </div>
  );
};
